/**
 * Server-state configuration.
 *
 * One decision worth stating: a 404 or a 403 is never retried. TanStack Query's default
 * retries every failure three times, which turns "this repository does not exist" into
 * three seconds of spinner before the same answer. Only genuinely transient failures are
 * worth repeating.
 */
import { QueryClient } from '@tanstack/react-query'

import { ApiError } from '@/lib/api/errors'

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry(failureCount, error) {
          if (failureCount >= 2) return false
          if (error instanceof ApiError) {
            return error.retryable || error.status >= 500 || error.status === 0
          }
          return false
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
      },
      mutations: { retry: false },
    },
  })
}

/** Query keys in one place, so an invalidation cannot miss a cache. */
export const queryKeys = {
  session: ['auth', 'session'] as const,
  profile: ['auth', 'profile'] as const,
  profileSessions: ['auth', 'profile', 'sessions'] as const,
  conversations: ['chat', 'conversations'] as const,
  credits: ['billing', 'credits'] as const,
  githubConnection: ['integrations', 'github'] as const,
  system: ['system'] as const,
  tools: ['tools'] as const,
  executions: (filters: unknown) => ['executions', filters] as const,
  unseenExecutions: ['executions', 'unseen'] as const,
  execution: (requestId: string) => ['execution', requestId] as const,
  metricsOverview: (hours: number) => ['metrics', 'overview', hours] as const,
  metricsTools: (hours: number) => ['metrics', 'tools', hours] as const,
  metricsErrors: (hours: number) => ['metrics', 'errors', hours] as const,
  repositories: (includeArchived: boolean) => ['repositories', includeArchived] as const,
  repository: (owner: string, repo: string) => ['repository', owner, repo] as const,
  repositoryIssues: (owner: string, repo: string, state: string) =>
    ['repository', owner, repo, 'issues', state] as const,
  repositoryLabels: (owner: string, repo: string) => ['repository', owner, repo, 'labels'] as const,
  benchmarkSummary: ['benchmark', 'summary'] as const,
  benchmarkRuns: (limit: number) => ['benchmark', 'runs', limit] as const,
  benchmarkRun: (runUid: string) => ['benchmark', 'run', runUid] as const,
  benchmarkResults: (runUid: string, filters: unknown) =>
    ['benchmark', 'run', runUid, 'results', filters] as const,
}
